import React, { Suspense, useState } from 'react';
import { Image } from '@youhelp-ru/ui';
import { MOBILE_BREAKPOINT_THRESHOLD } from '@cp/ds/src/theme';
import { Typography } from '@cp/ds/src/components/typography';
import { Button } from '@cp/ds/src/components/button';
import { Box } from '@cp/ds/src/components/box';
import { ArrowRightIcon } from '@cp/ds/src/icons/arrowRight';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { styled } from '@cp/ds/src/theme/styled';
import { SxProps } from '@mui/material';
import { RoutedLink } from 'src/shared/ui/link';
import SecurityCheckMark from '../../../../../../../assets/shapes/securityCheckMark.svg?url';
import image_562x500 from '../../../../../../../assets/images/homeChecksBannerPeople-375.png';
import image_398x500 from '../../../../../../../assets/images/homeChecksBannerPeople-1440.png';
import image_526x530 from '../../../../../../../assets/images/homeChecksBannerPeople-1920.png';
import { BannerBase } from '../bannerBase';
import { BANNER_MIN_HEIGHT_DESKTOP } from '../../const';

const TEXT_WIDTH_DESKTOP = 330;

const StyledSecurityCheckMark = styled(Image)(({ theme }) => {
  return {
    height: 'auto',
    [theme.breakpoints.down('md')]: {
      width: 72,
    },
    [theme.breakpoints.between('md', 'lg')]: {
      width: 54,
    },
    [theme.breakpoints.up('lg')]: {
      width: 72,
    },
  };
});

const renderPicture = ({ sx }: { sx?: SxProps; isMobile?: boolean; isMiddleScreen?: boolean } = {}) => (
  <Box sx={{ position: 'relative', width: { xs: 277, md: 195, xxl: 259 }, ...sx }}>
    <StyledSecurityCheckMark
      alt=""
      loading="lazy"
      src={SecurityCheckMark}
      sx={{
        color: 'green',
        width: { xs: 61, md: 44, xl: 61 },
        position: 'absolute',
        top: { xs: -6, md: -11, xl: -18 },
        left: { xs: 30, md: 'auto' },
        right: { md: 8, xl: -2 },
      }}
    />
    <Image
      alt="Проверка благонадежности"
      loading="lazy"
      sizes={`(max-width: 400px) 398px, (max-width: 915px) 562px, (max-width: 1920px) 526px, 398px`}
      src={image_398x500}
      srcSet={`${image_398x500} 398w, ${image_526x530} 526w, ${image_562x500} 562w`}
      style={{ width: '100%' }}
    />
  </Box>
);

const LazyCheckBannerModal = React.lazy(() => import('../../../modals/banners').then((module) => ({ default: module.CheckBannerModal })));

export const ChecksBanner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [bannerOpen, setOpen] = useState(false);

  return (
    <BannerBase
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: { [MOBILE_BREAKPOINT_THRESHOLD]: BANNER_MIN_HEIGHT_DESKTOP },
      }}
    >
      <Box
        sx={{
          [theme.breakpoints.down(MOBILE_BREAKPOINT_THRESHOLD)]: {
            width: '100%',
            textAlign: 'center',
          },
          [theme.breakpoints.up(MOBILE_BREAKPOINT_THRESHOLD)]: {
            maxWidth: TEXT_WIDTH_DESKTOP,
            mr: 2,
          },
        }}
      >
        <Typography
          component="div"
          sx={{
            typography: { xs: 'h5', [MOBILE_BREAKPOINT_THRESHOLD]: 'h4' },
          }}
        >
          Безопасность
        </Typography>
        <Typography
          component="div"
          sx={{
            mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 2 },
            typography: { xs: 'body bold', [MOBILE_BREAKPOINT_THRESHOLD]: 'h6' },
          }}
        >
          Все специалисты проходят проверку безопасности. Вы можете проверить своего специалиста{' '}
          <RoutedLink color="pink" href="https://spectrumdata.ru/" sx={{ display: 'inline', font: 'inherit' }}>
            тут
          </RoutedLink>
        </Typography>
        {isMobile && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5 }}>{renderPicture({ isMobile })}</Box>}
        <Box sx={{ textAlign: { xs: 'center', [MOBILE_BREAKPOINT_THRESHOLD]: 'left' } }}>
          <Button
            color="pink"
            iconRight={<ArrowRightIcon />}
            onClick={() => setOpen(true)}
            size={isMobile ? 'small' : 'normal'}
            sx={{ mt: 4, whiteSpace: 'nowrap', mx: { xs: 'auto', [MOBILE_BREAKPOINT_THRESHOLD]: 0 } }}
            variant="secondary"
          >
            Как мы проверяем
          </Button>
        </Box>
      </Box>

      {!isMobile && renderPicture({ sx: { flexShrink: 0 }, isMobile })}

      {bannerOpen && (
        <Suspense fallback={null}>
          <LazyCheckBannerModal bannerOpen={bannerOpen} setOpen={setOpen} />
        </Suspense>
      )}
    </BannerBase>
  );
};
